import PropTypes from 'prop-types';
import { useTranslate, useListContext } from 'react-admin';
import { Box } from '@mui/material';
import { useBarIndicatorsColors } from '@/composables/useBarIndicatorsColors';
import { Palette } from '@/themes/themeOverrides/Palette';
import { formatNumber } from '@/helpers/format/formatNumber.js';
import { getActiveBarIndex } from '@/helpers/analytics/charts.js';
import { styled } from '@mui/material/styles';
import { analyticsStyles } from '@/themes/commonStyles/analytics';
import { DEFAULT_PERIOD } from '@/components/analytics/PeriodFilters.jsx';

const colorGray = Palette.gray.gray300;
const colorRed = Palette.red.red800;
const colorOrange = Palette.yellow.yellow700;
const colorGreen = Palette.green.green500;

const StyledAnalyticsVerticalBars = styled(Box)(() => analyticsStyles.horizontalBars);

const ProductPackagesCountField = ({ record, className }) => {
    const { filterValues } = useListContext();
    const currentPeriod = filterValues?.period ?? DEFAULT_PERIOD;
    const analytics = record?.analytics?.[currentPeriod];
    const translate = useTranslate();
    const productsNumber = formatNumber(analytics?.packagesCount ?? 0, 0);
    const productsAvg = analytics?.packagesAvg ?? 0;
    const productsStdDev = analytics?.packagesStdDev ?? 0;
    const breakpoints = [productsAvg - productsStdDev, productsAvg, productsAvg + productsStdDev];
    const barColors = useBarIndicatorsColors(productsNumber, breakpoints, true);
    const percentDiff = productsAvg ? formatNumber(((productsNumber - productsAvg) / productsAvg) * 100, 0) : 0;

    if (!record.analytics) return null;

    let color;
    let text;
    const absPercentDiff = Math.abs(percentDiff);
    const activeBarIndex = getActiveBarIndex(productsNumber, breakpoints);

    if (productsNumber <= productsAvg - productsStdDev) {
        text = `${absPercentDiff}% ${translate('resources.product.labels.belowAverage')}`;
        color = colorRed;
    } else if (productsNumber > productsAvg - productsStdDev && productsNumber <= productsAvg) {
        text = `${absPercentDiff}% ${translate('resources.product.labels.belowAverage')}`;
        color = colorOrange;
    } else if (productsNumber > productsAvg && productsNumber <= productsAvg + productsStdDev) {
        text = `${absPercentDiff}% ${translate('resources.product.labels.aboveAverage')}`;
        color = colorGreen;
    } else {
        text = `${absPercentDiff}% ${translate('resources.product.labels.aboveAverage')}`;
        color = colorGreen;
    }

    return (
        <StyledAnalyticsVerticalBars className={className}>
            <Box className='bars-wrapper'>
                {barColors.map((color, i) => (
                    <Box
                        key={i}
                        className='bar'
                        sx={{
                            backgroundColor: i === activeBarIndex ? color : colorGray,
                        }}
                    />
                ))}
            </Box>
            <Box className='text-wrapper'>
                <Box className='text-number'>{productsNumber}</Box>
                <Box
                    className='text-value'
                    sx={{
                        color: color,
                    }}
                >
                    {text}
                </Box>
            </Box>
        </StyledAnalyticsVerticalBars>
    );
};

ProductPackagesCountField.propTypes = {
    record: PropTypes.shape({
        transactions: PropTypes.number,
    }),
    className: PropTypes.string,
};

export default ProductPackagesCountField;
